import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Bar,
  ReferenceLine,
  Tooltip
} from 'recharts';
import { DIFFICULTY_COLORS } from '../utils/constants';

// 색상 상수 정의 (Tailwind 디자인 토큰 값 사용)
/* eslint-disable no-restricted-syntax */
export const COLORS = {
  primary: '#4ABC85',       // primary color from tailwind.config.js
  primaryDark: '#3DA575',   // primary-hover from tailwind.config.js
  blue500: '#3B82F6',       // blue-500
  danger: '#DC2626',        // danger color from tailwind.config.js
  dangerLight: '#EF4444',   // danger-light from tailwind.config.js
  headerBg: '#231F20',      // headerBg from tailwind.config.js
  gray200: '#E5E7EB',       // gray-200 from tailwind.config.js
  gray400: '#9CA3AF',       // gray-400 from tailwind.config.js
  gray500: '#6B7280',       // gray-500 from tailwind.config.js
  gray600: '#4B5563',       // gray-600 from tailwind.config.js
  white: '#FFFFFF',         // white
  tooltipBorder: '#CCCCCC', // light gray for tooltip border
  shadowBlack: 'rgba(0,0,0,0.1)', // shadow
  shadowDanger: 'rgba(220, 38, 38, 0.6)' // danger shadow
};

// 진한 색상의 난이도별 색상 (Y축 레이블용)
const DIFFICULTY_COLORS_OPAQUE = [
  '#EAB308',  // Level 1 - yellow-600 (진한 노란색)
  '#059669',  // Level 2 - emerald-600 (진한 녹색)
  '#2563EB',  // Level 3 - blue-600 (진한 파란색)
  '#EA580C',  // Level 4 - orange-600 (진한 주황색)
  '#DC2626'   // Level 5 - red-600 (진한 빨간색)
];

// 레이팅에 따른 색상 결정 함수
const getRatingColor = (rating) => {
  if (!rating || rating === null) return COLORS.gray400;
  if (rating <= 2000) return DIFFICULTY_COLORS_OPAQUE[0]; // Lv1 색상
  if (rating <= 4000) return DIFFICULTY_COLORS_OPAQUE[1]; // Lv2 색상
  if (rating <= 6000) return DIFFICULTY_COLORS_OPAQUE[2]; // Lv3 색상
  if (rating <= 8000) return DIFFICULTY_COLORS_OPAQUE[3]; // Lv4 색상
  return DIFFICULTY_COLORS_OPAQUE[4]; // Lv5 색상
};
/* eslint-enable no-restricted-syntax */

const FormRatingChartSection = React.memo(({
  selectedStudent,
  selectedTest,
  getExamChartData,
  getYAxisDomain,
  reportData,
  onTestSelect,
  testHistory
}) => {
  const [tooltipActive, setTooltipActive] = React.useState(false);
  const originalChartData = React.useMemo(() => getExamChartData(selectedStudent), [getExamChartData, selectedStudent]);
  
  // Tooltip formatters - 컴포넌트 최상위에서 정의
  const labelFormatter = React.useCallback((label, payload) => {
    if (!payload || !payload.length || !payload[0] || !payload[0].payload) return '';
    
    const data = payload[0].payload;
    if (!data || !data.exam_id) return `${data?.exam_round || label}회차`;
    
    const accuracy = data.accuracy || 0;
    
    return (
      <div>
        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
          {data.exam_round}회차 (TID: {data.exam_id})
        </div>
        <div style={{ fontSize: '10px', color: COLORS.gray500, marginBottom: '2px' }}>
          {data.testDate ? new Date(data.testDate).toLocaleDateString('ko-KR') : ''}
        </div>
        <div style={{ color: COLORS.primary, fontWeight: 'bold', marginTop: '2px' }}>
          정답률: {accuracy}%
        </div>
      </div>
    );
  }, []);
  
  const formatter = React.useCallback((value, name, props) => {
    if (!props || !props.payload) return [`${value}`, name];
    
    if (name === 'rating') {
      if (value === null || value === undefined) return null;
      
      const { prevRating, ratingChange, ratingChangePercent, delta, difficulty, totalQuestions, exam_type } = props.payload;
      const changeColor = ratingChange > 0 ? COLORS.dangerLight : ratingChange < 0 ? COLORS.blue500 : COLORS.gray500;
      const changeSymbol = ratingChange > 0 ? '▲' : ratingChange < 0 ? '▼' : '';
      
      return [
        <div>
          <div style={{ color: COLORS.blue500, fontWeight: 'bold' }}>
            폼레이팅: {value.toLocaleString()}
          </div>
          {prevRating !== null && prevRating !== undefined && (
            <>
              <div style={{ marginLeft: '12px', color: COLORS.headerBg, marginTop: '2px', fontSize: '10px' }}>
                이전 점수: {prevRating.toLocaleString()}
              </div>
              <div style={{ marginLeft: '12px', color: COLORS.headerBg, marginTop: '2px' }}>
              전 회차 대비: {ratingChange !== 0 ? (
                  <span style={{ color: changeColor, fontSize: '10px' }}>
                    {changeSymbol}{Math.abs(ratingChange)} ({ratingChangePercent > 0 ? '+' : ''}{ratingChangePercent}%)
                  </span>
                ) : (
                  <span style={{ color: COLORS.gray500, fontSize: '10px' }}>변화 없음</span>
                )}
              </div>
            </>
          )}
          <div style={{ marginLeft: '12px', color: COLORS.headerBg, marginTop: '2px' }}>
          </div>
          {exam_type && (
            <div style={{ marginLeft: '12px', color: COLORS.headerBg, marginTop: '2px', fontSize: '10px' }}>
              시험 타입: {exam_type.toUpperCase()}
            </div>
          )}
          {totalQuestions && (
            <div style={{ marginLeft: '12px', color: COLORS.headerBg, marginTop: '2px', fontSize: '10px' }}>
              문제 수: {totalQuestions}개
            </div>
          )}
          {difficulty && (
            <div style={{ marginLeft: '12px', color: COLORS.headerBg, marginTop: '2px', fontSize: '10px' }}>
              평균 난이도: {difficulty.toFixed(1)}
            </div>
          )}
        </div>,
        ''
      ];
    } else if (name.startsWith('Level')) {
      if (value === 0 || value === null || value === undefined) return [null, ''];
      
      const { correctByLevel, totalByLevel, totalCorrect } = props.payload;
      if (!correctByLevel || !totalByLevel) return [null, ''];
      
      const levelIndex = parseInt(name.split(' ')[1]) - 1;
      const count = correctByLevel[levelIndex] || 0;
      const total = totalByLevel[levelIndex] || 0;
      const levelAccuracy = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
      const levelColor = DIFFICULTY_COLORS[levelIndex];
      
      return [
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '12px' }}>
          <span 
            style={{ 
              display: 'inline-block',
              width: '8px', 
              height: '8px', 
              backgroundColor: levelColor,
              borderRadius: '1px'
            }}
          ></span>
          <span style={{ color: COLORS.headerBg }}>
            Lv{levelIndex + 1}: {count}/{total}개 ({levelAccuracy}%)
          </span>
        </div>,
        ''
      ];
    }
    return [`${value}%`, name];
  }, []);
  
  // 최소 5개의 회차를 보장하기 위한 데이터 가공
  const chartData = React.useMemo(() => {
    if (originalChartData.length >= 5) {
      return originalChartData;
    }
    
    // 빈 데이터 포인트들을 추가하여 최소 5개 회차 보장
    const paddedData = [];
    for (let i = 1; i <= 5; i++) {
      const existingData = originalChartData.find(item => item.exam_round === i);
      if (existingData) {
        paddedData.push(existingData);
      } else {
        // 빈 데이터 포인트 추가
        paddedData.push({
          x: i,
          exam_round: i,
          rating: null,
          accuracy: null,
          level1: 0,
          level2: 0,
          level3: 0,
          level4: 0,
          level5: 0,
          hasRating: false
        });
      }
    }
    return paddedData;
  }, [originalChartData]);

  // 외부 클릭 시 툴팁 숨기기
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      // 막대 클릭 영역인지 확인
      const isBarClick = event.target.closest('rect[style*="cursor: pointer"]');
      if (isBarClick) {
        return; // 막대 클릭은 처리하지 않음
      }
      
      if (!event.target.closest('.recharts-wrapper')) {
        setTooltipActive(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    // touchstart 대신 touchend 사용하여 터치 완료 후 처리
    document.addEventListener('touchend', handleClickOutside);
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touchend', handleClickOutside);
    };
  }, []);
  
  return (
    <div className="relative rounded-3xl bg-white p-4 shadow-lg">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-[14px] font-bold text-gray-800">
          폼레이팅 변화 추이
          {selectedTest && <span className="ml-2 text-xs font-normal text-gray-600">({selectedTest.tree_name})</span>}
        </h3>
      </div>
      
      {/* 분리된 범례들 */}
      <div className="mb-3 flex items-start justify-between">
        {/* 좌측 상단: 폼레이팅 범례 */}
        <div className="flex items-center gap-1">
          <div className="h-2 w-3 rounded-3xl bg-blue-500"></div>
          <span className="text-[10px] font-medium text-gray-600">폼레이팅</span>
        </div>
        
        {/* 우측 상단: 난이도별 범례 */}
        <div className="flex items-center gap-3">
          {[1, 2, 3, 4, 5].map((level, index) => (
            <div key={level} className="flex items-center gap-1">
              <div 
                className="h-2 w-3 rounded-3xl" 
                style={{ backgroundColor: DIFFICULTY_COLORS[index] }}
              ></div>
              <span className="text-[9px] text-gray-600">Lv{level}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="h-44">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 10, right: 0, left: 0, bottom: 3 }}
              onMouseEnter={() => setTooltipActive(true)}
              onMouseMove={() => setTooltipActive(true)}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gray200} />
              <XAxis
                dataKey="exam_round"
                axisLine={false}
                tickLine={false}
                interval={0}
                minTickGap={10}
                tick={(props) => {
                  const { x, y, payload } = props;
                  const dataPoint = chartData.find(item => item.exam_round === payload.value);
                  const isSelected = selectedTest && dataPoint && (
                    dataPoint.exam_id === selectedTest.exam_id
                  );
                  
                  if (isSelected) {
                    return (
                      <g>
                        <text
                          x={x}
                          y={y}
                          dy={5}
                          textAnchor="middle"
                          fontSize={10}
                          fill={COLORS.danger}
                          fontWeight="bold"
                        >
                          {payload.value}
                        </text>
                        <text
                          x={x}
                          y={y}
                          dy={15}
                          textAnchor="middle"
                          fontSize={7}
                          fill={COLORS.danger}
                          fontWeight="bold"
                        >
                          회차
                        </text>
                      </g>
                    );
                  } else {
                    return (
                      <text
                        x={x}
                        y={y}
                        dy={5}
                        textAnchor="middle"
                        fontSize={10}
                        fill={COLORS.gray400}
                        fontWeight="normal"
                      >
                        {payload.value}
                      </text>
                    );
                  }
                }}
                dy={0}
                tickMargin={4}
              />
              <YAxis 
                yAxisId="left" 
                orientation="left" 
                domain={[0, 10000]} 
                axisLine={false} 
                tickLine={false} 
                ticks={[0, 2000, 4000, 6000, 8000, 10000]}
                tickCount={6}
                interval={0}
                tick={(props) => {
                  const { x, y, payload } = props;
                  const value = payload.value;
                  const color = getRatingColor(value);
                  const isBold = [2000, 4000, 6000, 8000].includes(value);
                  
                  return (
                    <text
                      x={x}
                      y={y}
                      textAnchor="end"
                      fontSize={8}
                      fill={color}
                      fontWeight={isBold ? 'bolder' : 'bold'}
                      dx={-2}
                    >
                      {value.toLocaleString()}
                    </text>
                  );
                }}
                width={32} 
                tickMargin={-5}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right" 
                domain={[0, 100]}
                axisLine={false} 
                tickLine={false} 
                tick={{ fontSize: 9, fill: COLORS.primary, fontWeight: 'bold' }} 
                width={20}
                tickMargin={-5}
              />
              <Tooltip
                active={tooltipActive}
                filterNull={true}
                contentStyle={{ 
                  fontSize: '11px', 
                  padding: '6px 8px', 
                  borderRadius: '6px', 
                  border: `1px solid ${COLORS.tooltipBorder}`,
                  backgroundColor: COLORS.white,
                  boxShadow: `0 2px 8px ${COLORS.shadowBlack}`,
                  zIndex: 10000,
                  position: 'relative',
                  transform: 'translateY(120px)',
                  minHeight: 'auto',
                  height: 'auto',
                  lineHeight: 1.2
                }}
                wrapperStyle={{ 
                  zIndex: 10000,
                  height: 'auto'
                }}
                itemStyle={{ 
                  color: COLORS.headerBg,
                  margin: 0,
                  padding: 0,
                  lineHeight: 1
                }}
                separator=""
                allowEscapeViewBox={{ x: false, y: false }}
                offset={0}
                isAnimationActive={false}
                labelFormatter={labelFormatter}
                formatter={formatter}
              />
              {[0, 1, 2, 3, 4].map((index) => (
                <Bar 
                  key={index}
                  yAxisId="right"
                  dataKey={`level${index + 1}`}
                  stackId="difficulty"
                                            fill={DIFFICULTY_COLORS[index]}
                          fillOpacity={0.7}
                          stroke="none"
                          strokeWidth={0}
                  name={`Level ${index + 1}`}
                  legendType="none"
                  isAnimationActive={false}
                  shape={(props) => {
                    const { x, y, width, height, payload, background } = props;
                    const isSelected = selectedTest && payload.exam_id === selectedTest.exam_id;
                    
                    // 전체 막대의 높이를 계산하기 위해 해당 회차의 모든 레벨 값 합산
                    const totalHeight = index === 0 && payload ? 
                      [1, 2, 3, 4, 5].reduce((sum, level) => {
                        const value = payload[`level${level}`] || 0;
                        return sum + (value > 0 ? (value / 100) * 176 : 0); // 차트 높이 176px 기준
                      }, 0) : 0;
                    
                    return (
                      <g>
                        {/* 선택된 회차의 배경 음영 (맨 아래 레벨에만 그리기) */}
                        {isSelected && index === 0 && (
                          <rect
                            x={x - width * 0.2}
                            y={0} // 차트 상단부터 시작
                            width={width * 1.4}
                            height={176} // 차트 높이를 약간 줄여서 하단 라운드가 보이도록
                            fill="rgba(239, 68, 68, 0.08)"
                            rx={6}
                            ry={6}
                          />
                        )}
                        {/* 원래 막대 */}
                        <rect
                          x={x}
                          y={y}
                          width={width}
                          height={height}
                          fill={DIFFICULTY_COLORS[index]}
                          fillOpacity={0.7}
                          rx={3} // 모든 막대에 라운드 처리
                          ry={3}
                          style={{ cursor: payload.exam_id ? 'pointer' : 'default' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (payload.exam_id && testHistory && onTestSelect) {
                              const test = testHistory.find(t => t.exam_id === payload.exam_id);
                              if (test) {
                                onTestSelect(test);
                              }
                            }
                          }}
                          onTouchEnd={(e) => {
                            e.stopPropagation();
                            e.preventDefault(); // 터치 이벤트가 클릭으로 변환되는 것 방지
                            if (payload.exam_id && testHistory && onTestSelect) {
                              const test = testHistory.find(t => t.exam_id === payload.exam_id);
                              if (test) {
                                onTestSelect(test);
                              }
                            }
                          }}
                        />
                        {/* 클릭 가능한 투명 영역 - 첫 번째 레벨(index === 0)에만 추가하여 중복 방지 */}
                        {index === 0 && payload.exam_id && (
                          <rect
                            x={x - width * 0.2}
                            y={0}
                            width={width * 1.4}
                            height={176} // 전체 차트 높이
                            fill="transparent"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (testHistory && onTestSelect) {
                                const test = testHistory.find(t => t.exam_id === payload.exam_id);
                                if (test) {
                                  onTestSelect(test);
                                }
                              }
                            }}
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (testHistory && onTestSelect) {
                                const test = testHistory.find(t => t.exam_id === payload.exam_id);
                                if (test) {
                                  onTestSelect(test);
                                }
                              }
                            }}
                          />
                        )}
                      </g>
                    );
                  }}
                />
              ))}
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="rating"
                stroke="rgba(59, 130, 246, 0.7)" // 파란색에 투명도 0.7 적용
                strokeWidth={2}
                isAnimationActive={false}
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  const rating = payload.rating;
                  const color = getRatingColor(rating);
                  
                  // 레이팅이 없으면 dot을 렌더링하지 않음
                  if (!rating || rating === null) return null;
                  
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={3}
                      fill={color}
                      stroke={COLORS.white}
                      strokeWidth={2}
                    />
                  );
                }}
                activeDot={(props) => {
                  const { cx, cy, payload } = props;
                  const rating = payload.rating;
                  const color = getRatingColor(rating);
                  
                  if (!rating || rating === null) return null;
                  
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={5}
                      fill={color}
                      stroke={COLORS.white}
                      strokeWidth={2}
                    />
                  );
                }}
                name="rating"
                legendType="none"
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-gray-500">
              {selectedTest ? '폼레이팅 데이터가 없습니다' : '테스트를 선택하세요'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

// getYAxisLabelColor 함수는 LearningStatus.js에서 직접 정의하여 사용

export default FormRatingChartSection; 